import axios, { AxiosError, CancelTokenSource } from 'axios';
import { ApiUrls } from '../constants/apiUrls'
import { ApiResponseHelper } from '../infrastructure/utils/ApiResponseHelper'
import { IApiResponse } from '../models/ApiResponseModels'
import { ParameterTempleteInformation, TubeTaskTagTemplete } from '../models/AppealModels'
import { AppealAdditionalTag, AppealData, AppealTag, AppealTagParameter, AppealTopic, CreateTaskTagCommand, ShortAppealData } from '../models/AppealReferanceModels';


export class AppealRequests {
    private cancelTokenSource?: CancelTokenSource


    public Init(): void {
        this.cancelTokenSource = axios.CancelToken.source();
    }

    public Abort(): void {
        this.cancelTokenSource?.cancel();

    }


    public async GetParameterTempleteInformation(parameterTempleteId: string): Promise<IApiResponse<ParameterTempleteInformation | null>> {


        return await axios.get<IApiResponse<ParameterTempleteInformation | null>>(`${ApiUrls.GetParameterTempleteInformation}?parameterTempleteId=${parameterTempleteId}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response => {
                return response.data;
            })
            .catch((error: AxiosError) => {
                if (axios.isCancel(error)) {
                    return { result: true, data: null, error: null, messageId: null }
                }
                return { result: false, data: null, error: ApiResponseHelper.GeneratorAxiosError(error), messageId: null }
            })
    }


    public async GetAppealTopics(): Promise<IApiResponse<AppealTopic[]>> {


        return await axios.get<IApiResponse<AppealTopic[]>>(`${ApiUrls.GetAppealTopics}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response => {
                return response.data;
            })
            .catch((error: AxiosError) => {
                if (axios.isCancel(error)) {
                    return { result: true, data: [], error: null, messageId: null }
                }
                return { result: false, data: [], error: ApiResponseHelper.GeneratorAxiosError(error), messageId: null }
            })
    }

    public async GetAppealTags(appealTopicId?: string): Promise<IApiResponse<AppealTag[]>> {


        return await axios.get<IApiResponse<AppealTag[]>>(`${ApiUrls.GetAppealTags}?${appealTopicId !== null ? `appealTopicId=${appealTopicId}` : ""}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response => {
                return response.data;
            })
            .catch((error: AxiosError) => {
                if (axios.isCancel(error)) {
                    return { result: true, data: [], error: null, messageId: null }
                }
                return { result: false, data: [], error: ApiResponseHelper.GeneratorAxiosError(error), messageId: null }
            })
    }

    public async GetAppealAdditionalTags(appealTagId?: string): Promise<IApiResponse<AppealAdditionalTag[]>> {

        return await axios.get<IApiResponse<AppealAdditionalTag[]>>(`${ApiUrls.GetAppealAdditionalTags}?${appealTagId !== null ? `appealTopicId=${appealTagId}` : ""}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response => {
                return response.data;
            })
            .catch((error: AxiosError) => {
                if (axios.isCancel(error)) {
                    return { result: true, data: [], error: null, messageId: null }
                }
                return { result: false, data: [], error: ApiResponseHelper.GeneratorAxiosError(error), messageId: null }
            })
    }

    public async GetAccountAppeals(personalAccountId: string): Promise<{ personalAccountId: string, response: IApiResponse<ShortAppealData[]> }> {

        return await axios.get<IApiResponse<ShortAppealData[]>>(`${ApiUrls.GetAppeals}?${personalAccountId !== null ? `personalAccountId=${personalAccountId}` : ""}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response => ({
                personalAccountId: personalAccountId,
                response: response.data
            }))
            .catch((error: AxiosError) => ({
                personalAccountId: personalAccountId,
                response: ApiResponseHelper.GetDefaultArrayResponse<ShortAppealData>()
            }))
    }

    public async GetAccountAppealData(taskTagId: string): Promise<IApiResponse<AppealData | null>> {

        return await axios.get<IApiResponse<AppealData>>(`${ApiUrls.GetAppealInformation}?${taskTagId !== null ? `taskTagId=${taskTagId}` : ""}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response =>
                response.data
            )
            .catch((error: AxiosError) =>
                ApiResponseHelper.GetDefaultObjectResponse<AppealData>()
            )
    }

    public async GetAppealTagParameterInformation(appealTagId?: string): Promise<IApiResponse<AppealTagParameter[] | null>> {

        return await axios.get<IApiResponse<AppealTagParameter[]>>(`${ApiUrls.GetAppealTagParameterInformation}?${appealTagId !== null ? `appealTagId=${appealTagId}` : ""}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response =>
                response.data
            )
            .catch((error: AxiosError) =>
                ApiResponseHelper.GetDefaultArrayResponse<AppealTagParameter>()
            )
    }

    public async UploadAppealClientFile(fileName: string, formFile: File, index: number): Promise<{ index: number, response: IApiResponse<string> }> {

        const formData = new FormData;

        formData.append("file", formFile);
        return await axios.post<IApiResponse<string>>(`${ApiUrls.UploadAppealClientFile}`, formData,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response => {
                return { index: index, response: response.data }
            })
            .catch((error: AxiosError) => {
                return { index: index, response: { result: false, data: "", error: null, messageId: null } }

            })
    }

    public async GetAppealClientFile(fileId: string): Promise<IApiResponse<any>> {

        return await axios.get<IApiResponse<any>>(`${ApiUrls.GetAppealClientFile}?${`fileId=${fileId}`}`,
            { responseType: "blob", cancelToken: this.cancelTokenSource?.token })
            .then(response =>
                response.data
            )
            .catch((error: AxiosError) => {
                if (axios.isCancel(error)) {
                    return { result: true, data: null, error: null, messageId: null }
                }
                return { result: false, data: null, error: ApiResponseHelper.GeneratorAxiosError(error), messageId: null }
            })
    }

    public async CreateAppeal(command: CreateTaskTagCommand): Promise<IApiResponse<{ taskTagId: string, taskTagSerialNumber: number }>> {
        return await axios.post<IApiResponse<{ taskTagId: string, taskTagSerialNumber: number }>>(`${ApiUrls.CreateTaskDialog}`,
            command,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response =>
                response.data
            )
            .catch((error: AxiosError) => { return { result: false, data: { taskTagId: "", taskTagSerialNumber: 0 }, error: null, messageId: null } }
            )
    }

    public async SetQuality(taskTagId: string, quality: number, comment: string): Promise<IApiResponse<{}>> {
        return await axios.post<IApiResponse<{}>>(`${ApiUrls.SetQuailty}`,
            {
                taskTagId: taskTagId,
                quality: quality,
                comment: comment
            },
            { cancelToken: this.cancelTokenSource?.token })
            .then(response =>
                response.data
            )
            .catch((error: AxiosError) => { return { result: false, data: {}, error: null, messageId: null } }
            )
    }


    public async GetTubeTaskTagTemplete(): Promise<IApiResponse<TubeTaskTagTemplete | null>> {

        return await axios.get<IApiResponse<TubeTaskTagTemplete>>(`${ApiUrls.GetTubeTaskTagTemplete}`,
            { cancelToken: this.cancelTokenSource?.token })
            .then(response =>
                response.data
            )
            .catch((error: AxiosError) => { return { result: false, data: null, error: null, messageId: null } }
            )
    }
}


{/*
    "dialogSourceId": "3a8c5826-2025-4499-87ae-f5db164412c7",
    "applicantClientId": 4952245,
    "applicantContactClientId": "5",
    "commentDialog": null,
    "TaskAppeals": [
                        {
                            "appealTopicId": "8ae29166-0591-4275-aaaf-c3ed3105ad36",
                            "serviceObjectId": "002672978",
                            "serviceObjectSubdivisonName": "ОРН",
                            "recipientClientId": 4952246,
                            "recipientContactClientId": "5",
                            "commentAppeal": null,
                            "TaskTags": [
                                {
                                    "appealTagId": "4fc41ac3-d123-4c61-8e95-42e115b20531",
                                    "dateExecution": "2023-09-11T18:31:47.813+03:00",
                                    "commentTag": null,
                                    "routTempleteID": "f72e254e-6596-4f1c-8255-b51a2df4f365",
                                    "parameterTempleteId": "88214dfd-0179-41ea-90e0-175df56db52b",
                                    "appealAdditionalTagsId": [],
                                    "parameters": {
                                        "clientname": {
                                            "text": "Георгий Владимирович Якунин"
                                        },
                                        "clientphone": {
                                            "text": "+79952357252"
                                        },
                                        "quantity": {
                                            "text": "2"
                                        },
                                        "emailclient": {
                                            "text": "bighorik@yandex.ru"
                                        },
                                        "feedbackmailtext": {
                                            "text": null
                                        },
                                        "feedbackmailfiles": {
                                            "files": [
                                                {
                                                    "id": "8d9c1249-8407-4fd2-b79e-d38c821f256a",
                                                    "name": "file-folder-icon-on-marketingpersonalities.com_-1536x1178.jpg"
                                                }
                                            ]
                                        }
                                    }
                                }
                            ]
                        }
                    ],
                        "sourceEntity": "",
                            "sourceSystem": null
                */}